import { set as cookieSet, get as cookieGet, remove as cookieRemove } from "js-cookie";
import StorageBackend from '../StorageBackend';
import TemsPolicy, { ViewModelUserLevel } from './TemsPolicy';

export interface RecorderUser {
    UserName: string;
    Level: ViewModelUserLevel;
    StartTime?: Date;
    StopTime?: Date;
}

export interface LoginSuccessResponse {
    readonly success: true;
    readonly userLevel: ViewModelUserLevel;
    readonly loginRequired: boolean;
    readonly token: string;
};

interface LoginFailedResponse {
    readonly success: false;
    readonly rawResponse?: any;
}

export type LoginResponse = LoginSuccessResponse | LoginFailedResponse;

export default class Authorization {
    private tokenRefreshTimer;
    private changeUserHandlers: ((user: RecorderUser) => void)[] = [];
    private shouldTryAutologin = true;


    public constructor() {
        this.setTokenRefreshTimer();
    }

    public autoLogin(policies: TemsPolicy[]): Promise<LoginResponse|null> {
        var token = cookieGet("_temsToken");

        if (this.meetsPolicy(token, policies)) {
            return Promise.resolve(<LoginSuccessResponse>{
                success: true,
                token,
                userLevel: null,
                loginRequired: false
            });
        }

        if (policies.length == 1 && policies[0] == TemsPolicy.OperatorPolicy && this.shouldTryAutologin) {
            this.shouldTryAutologin = false;
            return this.login("Operator", "").then((response) => {
                if (response.success) {
                    this.shouldTryAutologin = true;
                }
                return response;
            });
        }

        return Promise.resolve(<LoginFailedResponse>{
            success: false
        });
    }

    public authorize(...policies: TemsPolicy[]) {
        return this.meetsPolicy(cookieGet("_temsToken"), policies);
    }

    public logout() {
        clearTimeout(this.tokenRefreshTimer);
        return cookieRemove("_temsToken");
    }

    public onChangeUser(callback: (user: RecorderUser) => void) {
        this.changeUserHandlers.push(callback);
    }

    public getCurrentUser(): RecorderUser {
        var tokenString = cookieGet("_temsToken");
        var token = this.parseToken(tokenString);
        return {
            Level: ViewModelUserLevel[token.tems_userlevel],
            UserName: token.userName
        };
    }

    public login(userName: string, password: string): Promise<LoginResponse> {
        return fetch(`/user/${userName}/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: 'Bearer ' + cookieGet('_temsToken')
            },
            body: "password=" + encodeURIComponent(password)
        }).then((response: Response) => {
            switch (response.status) {
                case 200:
                    return response.json().then((data) => {
                        var parsedToken = this.parseToken(data.token);
                        var stringUserlevel: string = parsedToken.tems_userlevel;
                        this.changeToken(data.token);

                        var token = this.parseToken(data.token);
                        let recorderTimeOffset = parseInt(token.tems_login_date) - Math.floor(Date.now() / 1000);
                        StorageBackend.setValue("RecorderTimeOffsetSeconds", recorderTimeOffset.toString());

                        var userLevel: ViewModelUserLevel = ViewModelUserLevel[stringUserlevel];
                        const result : LoginSuccessResponse = {
                            success: true,
                            token: data.token,
                            userLevel,
                            loginRequired: true
                        };
                        return result;
                    });
                case 500:
                    return Promise.resolve(<LoginResponse>{
                        success: false
                    });
                default:
                    return response.json().then((data) => {
                        return <LoginResponse>{
                            success: false,
                            rawResponse: data
                        };
                    });
            }
        });
    }

    private meetsPolicy(tokenString: string, policies: TemsPolicy[]): boolean {
        if (!tokenString) {
            return false;
        }

        var token = this.parseToken(tokenString);
        var recorderTime = this.estimateRecorderTime();

        if (token.exp < recorderTime) {
            return false;
        }

        function CheckPolicy(policy: TemsPolicy) {
            if (policy >= TemsPolicy.OperatorPolicy && policy <= TemsPolicy.DeveloperPolicy) {
                return TemsPolicy[token.tems_userlevel + "Policy"] >= policy;
            } else if (policy == TemsPolicy.ConfirmedPolicy) {
                return recorderTime.getTime() - new Date(parseInt(token.tems_login_date) * 1000).getTime() > 15000; // No longer than 15 seconds
            }

            throw "Unkown Policy";
        }

        for (let i = 0; i < policies.length; i++) {
            if (!CheckPolicy(policies[i])) return false;
        }

        return true;
    }

    private setTokenRefreshTimer() {
        var tokenString = cookieGet("_temsToken");

        if (this.meetsPolicy(tokenString, [TemsPolicy.OperatorPolicy])) {
            var token = this.parseToken(tokenString);
            var millisecondsTillExpiration = token.exp.getTime() - this.estimateRecorderTime().getTime();

            window.clearTimeout(this.tokenRefreshTimer);
            this.tokenRefreshTimer = window.setTimeout(() => {
                fetch("/user/refreshtoken", {
                    headers: {
                        Authorization: 'Bearer ' + tokenString
                    }
                }).then((response) => {
                    if (response.ok) {
                        response.json().then(e => {
                            if (e.token) {
                                this.changeToken(e.token);
                            }
                        });
                    }
                });
            }, millisecondsTillExpiration - 30000);
        }
    }

    private changeToken(newTokenString: string) {
        var oldTokenString = cookieGet("_temsToken");
        if (oldTokenString != newTokenString) {
            cookieSet("_temsToken", newTokenString);
            this.setTokenRefreshTimer();

            if (!oldTokenString || this.parseToken(oldTokenString).userName != this.parseToken(newTokenString).userName) {
                var newUser = this.getCurrentUser();
                for (let i = 0; i < this.changeUserHandlers.length; i++) {
                    this.changeUserHandlers[i](newUser);
                }
            }
        }
    }

    private estimateRecorderTime() {
        var serverOffset = StorageBackend.getValue("RecorderTimeOffsetSeconds");
        return new Date(
            new Date().getTime() + parseInt(serverOffset) * 1000
        );
    }

    private parseToken(token): TemsJwtToken {
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace('-', '+').replace('_', '/');
        var raw = JSON.parse(window.atob(base64));
        raw.userName = raw["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
        raw.exp = new Date(raw.exp * 1000);
        return raw;
    }
}

interface TemsJwtToken {
    exp: Date;
    tems_userlevel: string;
    tems_login_date: string;
    userName: string
}